import React from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { PAC_DATABASE } from "../lib/pacDatabase";
import { AlertTriangle, CheckCircle2, Cpu, Settings2 } from "lucide-react";

const round1 = (n) => Math.round(n * 10) / 10;
const round2 = (n) => Math.round(n * 100) / 100;

/**
 * Étape 4 — Sélection de la PAC compatible.
 * Filtre les machines de pacDatabase selon la plage de couverture choisie à
 * l'étape Émetteurs : une machine est compatible si sa puissance à -7°C
 * couvre au moins la BORNE BASSE de la plage. La machine "Recommandée"
 * est la plus petite qui couvre la BORNE HAUTE.
 */
export default function PacSelector({
  deperditionsCalcKw,
  deperditionsOverrideKw,
  setDeperditionsOverrideKw,
  selectedPacId,
  setSelectedPacId,
  couvertureRange = "100-120",
}) {
  const usedDeperditions = deperditionsOverrideKw ?? deperditionsCalcKw;
  const [minPct, maxPct] = couvertureRange.split("-").map(Number);
  const minRequiredKw = round2((usedDeperditions * minPct) / 100);
  const recoTargetKw  = round2((usedDeperditions * maxPct) / 100);

  // Compatibles : couvre au moins la borne basse.
  const matches = PAC_DATABASE
    .filter((p) => p.powerAtMinus7 >= minRequiredKw)
    .map((p) => ({
      ...p,
      couverturePct: round1((p.powerAtMinus7 / usedDeperditions) * 100),
      surdimensionnementPct: round1(((p.powerAtMinus7 / recoTargetKw) - 1) * 100),
    }))
    .sort((a, b) => a.powerAtMinus7 - b.powerAtMinus7);

  // Recommandée = la plus petite qui couvre la borne haute, sinon la 1ère.
  const recommended =
    matches.find((p) => p.powerAtMinus7 >= recoTargetKw) || matches[0] || null;
  const isOverCapacity = matches.length === 0;

  // Auto-sélection si rien de choisi ou choix devenu invalide.
  React.useEffect(() => {
    if (!recommended) return;
    if (!selectedPacId || !matches.find((m) => m.id === selectedPacId)) {
      setSelectedPacId(recommended.id);
    }
  }, [recommended?.id, matches.length, selectedPacId, setSelectedPacId]);

  const handleOverride = (v) => {
    if (v === "" || v == null) {
      setDeperditionsOverrideKw(null);
      return;
    }
    const n = parseFloat(String(v).replace(",", "."));
    setDeperditionsOverrideKw(Number.isFinite(n) && n > 0 ? n : null);
  };

  return (
    <div className="space-y-5" data-testid="pac-selector-step">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-9 w-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
          <Cpu className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-display font-bold text-slate-900 text-lg">
            Sélection de la PAC
          </h3>
          <p className="text-sm text-slate-500">
            Modèles compatibles à -7&nbsp;°C extérieur (régime 55&nbsp;°C, Zone&nbsp;H1).
            Plage de couverture&nbsp;: <span className="font-mono-data font-semibold text-slate-700">{minPct} – {maxPct}&nbsp;%</span>
          </p>
        </div>
      </div>

      {/* Déperditions input — pré-rempli, modifiable */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="pac-label">
            Déperditions à -7&nbsp;°C (kW)
          </Label>
          <Input
            type="number"
            min="0"
            step="0.1"
            value={deperditionsOverrideKw ?? deperditionsCalcKw}
            onChange={(e) => handleOverride(e.target.value)}
            className="h-11 mt-1.5 font-mono-data bg-white"
            data-testid="input-deperditions-override"
          />
          <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1">
            <Settings2 className="h-3 w-3" />
            Valeur calculée :{" "}
            <span className="font-mono-data text-slate-700">
              {deperditionsCalcKw} kW
            </span>
            {deperditionsOverrideKw != null &&
              deperditionsOverrideKw !== deperditionsCalcKw && (
                <button
                  type="button"
                  className="ml-2 text-blue-600 hover:underline"
                  onClick={() => setDeperditionsOverrideKw(null)}
                  data-testid="reset-deperditions-btn"
                >
                  Réinitialiser
                </button>
              )}
          </p>
        </div>
        <div>
          <Label className="pac-label">
            Puissance cible ({maxPct}&nbsp;%)
          </Label>
          <div
            className="h-11 mt-1.5 px-3 flex items-center rounded-lg border border-blue-200 bg-blue-50/60 font-mono-data text-blue-900 font-semibold"
            data-testid="puissance-cible"
          >
            {recoTargetKw} kW
          </div>
          <p className="text-xs text-slate-500 mt-1.5">
            Borne basse acceptée&nbsp;: <span className="font-mono-data text-slate-700">{minRequiredKw} kW</span> ({minPct}&nbsp;%)
          </p>
        </div>
      </div>

      {/* Over-capacity warning */}
      {isOverCapacity && (
        <div
          className="rounded-xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3"
          data-testid="pac-over-capacity-alert"
        >
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-amber-900">
            <div className="font-display font-semibold mb-1">
              Aucune machine ne couvre les déperditions
            </div>
            La puissance minimale requise ({minRequiredKw}&nbsp;kW à {minPct}&nbsp;%)
            dépasse la capacité du plus gros modèle disponible. Envisagez une{" "}
            <strong>configuration en cascade</strong> (deux PAC en parallèle) ou{" "}
            <strong>améliorez l'isolation</strong> pour réduire les déperditions.
          </div>
        </div>
      )}

      {/* Compatible matches list */}
      {matches.length > 0 && (
        <div className="space-y-3" data-testid="pac-matches-list">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">
            Machines compatibles ({matches.length})
          </div>
          {matches.map((pac) => {
            const isRecommended = recommended && pac.id === recommended.id;
            const isSelected = pac.id === selectedPacId;
            return (
              <button
                key={pac.id}
                type="button"
                onClick={() => setSelectedPacId(pac.id)}
                data-testid={`pac-option-${pac.id}`}
                className={`w-full text-left rounded-xl border-2 p-4 transition-all ${
                  isSelected
                    ? "border-blue-500 bg-blue-50/60 ring-2 ring-blue-200"
                    : "border-slate-200 bg-white hover:border-blue-300"
                }`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-mono-data text-slate-500 uppercase tracking-wider">
                        {pac.brand}
                      </span>
                      {isRecommended && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-2 py-0.5 rounded-full"
                          data-testid="badge-recommande"
                        >
                          <CheckCircle2 className="h-3 w-3" />
                          Recommandé
                        </span>
                      )}
                      {!isRecommended && (
                        <span className="text-[10px] uppercase tracking-wider font-medium bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                          Alternative
                        </span>
                      )}
                    </div>
                    <div className="mt-1 font-display font-semibold text-slate-900 text-base">
                      {pac.model}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Écart vs cible ({maxPct}&nbsp;%) :{" "}
                      <span className="font-mono-data">
                        {pac.surdimensionnementPct >= 0 ? "+" : ""}
                        {pac.surdimensionnementPct}&nbsp;%
                      </span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-mono-data text-2xl font-bold text-slate-900">
                      {pac.powerAtMinus7}{" "}
                      <span className="text-sm text-slate-500 font-normal">kW</span>
                    </div>
                    <div className="text-xs text-slate-500">à -7&nbsp;°C</div>
                    <div className="mt-1 text-xs font-mono-data font-semibold text-emerald-700">
                      Couverture {pac.couverturePct}&nbsp;%
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
